import { Fragment, useEffect, useState } from 'react';
import {
  Typography,
  Container,
  Grid,
  styled,
  Paper,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import Page from '../components/Page';
import { store } from '../store';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCreatedPlansList,
  getPlanesByPeriod,
} from '../features/planes/planesSlice';
import { CreatedPlans } from '../features/planes/planesAPI';
import { Cuota } from '../features/cuentacorriente/ctaCorrienteAPI';
import { ImponibleData } from '../features/imponibles/imponiblesAPI';

const MainWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  display: 'flex',
  flexDirection: 'column',
  padding: '2rem',
}));

const CustomButton = styled(Button)(() => ({
  color: 'white',
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
}));

const PlanButton = styled(Paper)(() => ({
  border: '1.5px solid',
  width: '100%',
  color: 'white',
  padding: '1rem 1.5rem',
  fontSize: '.75rem',
  fontWeight: '700',
  marginBottom: '1rem',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

interface PlanState {
  selectedCuotas: Cuota[];
  resourceNumber: string;
  imponible: ImponibleData;
}

export default function Plans() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { selectedCuotas: cuotas, resourceNumber, imponible } = state as PlanState;

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const plans = useSelector(getCreatedPlansList);

  const [selectedPlan, setSelectedPlan] = useState<CreatedPlans | null>(null);
  const [periodos, setPeriodos] = useState<string[]>([]);
  const [selectedPeriodos, setSelectedPeriodos] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    store.dispatch(getPlanesByPeriod({ selectedCuotas: cuotas, resourceNumber }))
      .unwrap()
      .finally(() => {
        setIsLoading(false);
      });

    const newPeriodos = cuotas.map(function (cuota) {
      return ('0' + cuota.cuota).slice(-2) + '/' + cuota.anio;
    });

    setSelectedPlan(null);
    setPeriodos(newPeriodos);
  }, []);

  useEffect(() => {
    if (!selectedPlan) {
      setSelectedPeriodos([]);
      return;
    }

    const newPeriodos = periodos.filter(periodo => {
      const año = periodo.split('/')[1];
      const parsedAño = parseInt(año);

      return selectedPlan.anio_ini_incl <= parsedAño && parsedAño <= selectedPlan.anio_fin_incl;
    });

    setSelectedPeriodos(newPeriodos);
  }, [selectedPlan, periodos]);

  const handleContinue = () => {
    navigate('/planes/propuesta-plan', {
      state: {
        selectedCuotas: cuotas,
        selectedPlan,
        resourceNumber,
        imponible,
      },
    });
  };

  const handleGoBack = () => {
    navigate(`/imponibles/${imponible.id}`);
  };

  return (
    <Page>
      <Container>
        <Grid
          container
          padding={'1.875rem'}
          justifyContent="center"
          direction={desktop ? 'row' : 'column'}
        >
          <Grid item sm={10} sx={{ alignSelf: 'center' }}>
            <Box mb={3}>
              <MainWrapper>
                <Grid container direction="row">
                  <Grid item md={8}>
                    <Typography variant="subtitle1" mb="1rem">Solicitud de Plan de Pago</Typography>
                    <Typography variant="body1" mb="1rem">
                      Se está solicitando un plan de pago por{' '}
                      {
                        periodos.length > 1
                          ? `toda la deuda comprendida entre la cuota ${periodos[0]} y la cuota ${periodos.slice(-1)}.`
                          : `la cuota ${periodos[0]}.`
                      }
                    </Typography>
                    <Typography variant="body1" mb="1rem">
                      Condiciones de caducidad del presente convenio de pago:
                    </Typography>
                    <Typography variant="body1" mb="1rem">(Art. 35 Fiscal Vigente)</Typography>
                    <Typography variant="body1" mb="1rem">
                      *la falta de pago de (1) una cuota por el período de (90) noventa días, dará lugar a la caducidad
                      del plan de pagos concedido, así como la falta de pago de (3) tres cuotas correspondientes al año
                      fiscal en curso de los tributos y tasas.
                    </Typography>
                    <Typography variant="body1" mb="1rem">
                      Recuerde que una vez confirmado el plan de pago interrumpe la prescripción de los períodos
                      seleccionados.
                    </Typography>
                    <Typography><strong>Períodos seleccionados:</strong> {
                      periodos.map((periodo, idx) => (
                        <Fragment key={periodo}>
                          {
                            selectedPeriodos.includes(periodo)
                              ? <span style={{ color: theme.palette.primary.main }}>{periodo}</span>
                              : <>{periodo}</>
                          }
                          {idx + 1 < periodos.length ? ', ' : ''}
                        </Fragment>
                      ))
                    }</Typography>
                    {
                      selectedPeriodos.length > 0 && periodos.length > selectedPeriodos.length
                        ? <Alert severity="warning" sx={{ marginTop: '1rem', fontSize: '.8rem' }}>
                          El plan seleccionado no cubre todos los periodos. Una vez finalizada la creación del mismo
                          se le ofrecerá la opción de crear un plan para los periodos restantes.
                        </Alert>
                        : null
                    }
                  </Grid>
                  <Grid item md={4} pl={5} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" mb="1rem" sx={{ textAlign: 'center' }} >Elegí tu plan</Typography>
                      {
                        isLoading
                          ? <CircularProgress size={20} />
                          : Object.values(plans).map(plan => (
                            console.log(plans),
                            <PlanButton
                              key={plan.id}
                              onClick={() => setSelectedPlan(plan)}
                              sx={{
                                borderColor: plan === selectedPlan ? '#36B7E8' : '#E4E6EF',
                                backgroundColor: plan === selectedPlan ? 'rgba(54, 183, 232, 0.1)' : 'transparent',
                              }}
                            >
                              <Box sx={{ width: '100%' }}>
                                <Typography>
                                  {
                                    plan.limite_cant_cuotas === 1
                                      ?
                                      <>
                                        Cancelación de Contado
                                        <br />
                                        <small>Descuento de intereses: {plan.porc_dto_interes} %</small>
                                      </>
                                      : <>
                                        Cancelación entre {plan.minimo_cant_cuotas} y {plan.limite_cant_cuotas}
                                        {' '}cuotas
                                        <br />
                                        <small>Descuento de intereses: {plan.porc_dto_interes}%</small>
                                      </>
                                  }
                                  {
                                    plan.anio_ini_incl === plan.anio_fin_incl
                                      ? <>
                                        <br />
                                        <small>
                                          Plan válido únicamente para cuotas del año {plan.anio_ini_incl}
                                        </small>
                                      </>
                                      : null
                                  }
                                </Typography>
                              </Box>
                            </PlanButton>
                          ))
                      }
                    </Box>
                  </Grid>
                </Grid>

                <Box my={3} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <CustomButton onClick={handleGoBack} variant="contained">
                    Volver
                  </CustomButton>
                  <Box>
                    {
                      selectedPlan !== null
                        ? <CustomButton onClick={handleContinue} variant="contained">
                          Continuar
                        </CustomButton>
                        : null
                    }
                  </Box>
                </Box>
              </MainWrapper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
